import { useCallback, useEffect, useRef, useState } from 'react';
import { X, Crop, Maximize2 } from 'lucide-react';
import {
  canvasToPngFile,
  cropCanvas,
  renderPdfPageToCanvas,
} from '../utils/floorPlanPdf';

function clampRect(x1, y1, x2, y2) {
  const left = Math.min(x1, x2);
  const top = Math.min(y1, y2);
  const right = Math.max(x1, x2);
  const bottom = Math.max(y1, y2);
  return { left, top, width: right - left, height: bottom - top };
}

/**
 * Preview a PDF floor plan, use full page or drag a region ??PNG for plan_url storage.
 */
export default function FloorPlanPdfCropModal({ file, title = 'PDF floor plan', onConfirm, onClose }) {
  const wrapRef = useRef(null);
  const sourceCanvasRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState(1);
  const [pageNum, setPageNum] = useState(1);
  const [displaySize, setDisplaySize] = useState({ w: 0, h: 0 });
  const [drag, setDrag] = useState(null);
  const [selection, setSelection] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadPage = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    setSelection(null);
    setDrag(null);
    try {
      const { canvas, numPages: total } = await renderPdfPageToCanvas(file, pageNum, 2);
      sourceCanvasRef.current = canvas;
      setNumPages(total);
      const maxW = Math.min(900, window.innerWidth - 80);
      const scale = maxW / canvas.width;
      setDisplaySize({
        w: Math.round(canvas.width * scale),
        h: Math.round(canvas.height * scale),
      });
    } catch (e) {
      const msg = e?.message || 'Could not read PDF';
      setError(
        /worker|dynamically imported/i.test(msg)
          ? 'PDF viewer failed to load (worker). Redeploy frontend + reload nginx, or try again — CDN fallback should apply after refresh.'
          : msg
      );
      sourceCanvasRef.current = null;
    } finally {
      setLoading(false);
    }
  }, [file, pageNum]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  const mapToCanvas = (clientX, clientY) => {
    const wrap = wrapRef.current;
    const canvas = sourceCanvasRef.current;
    if (!wrap || !canvas) return { x: 0, y: 0 };
    const rect = wrap.getBoundingClientRect();
    const relX = clientX - rect.left;
    const relY = clientY - rect.top;
    const sx = canvas.width / rect.width;
    const sy = canvas.height / rect.height;
    return {
      x: Math.max(0, Math.min(canvas.width, relX * sx)),
      y: Math.max(0, Math.min(canvas.height, relY * sy)),
    };
  };

  const onPointerDown = (e) => {
    if (!sourceCanvasRef.current) return;
    const p = mapToCanvas(e.clientX, e.clientY);
    setDrag({ startX: p.x, startY: p.y, endX: p.x, endY: p.y });
    setSelection(null);
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const p = mapToCanvas(e.clientX, e.clientY);
    setDrag((d) => ({ ...d, endX: p.x, endY: p.y }));
  };

  const onPointerUp = () => {
    if (!drag) return;
    const r = clampRect(drag.startX, drag.startY, drag.endX, drag.endY);
    if (r.width > 8 && r.height > 8) {
      setSelection(r);
    }
    setDrag(null);
  };

  const overlayRect = () => {
    const canvas = sourceCanvasRef.current;
    if (!canvas || !displaySize.w) return null;
    const sx = displaySize.w / canvas.width;
    const sy = displaySize.h / canvas.height;
    if (drag) {
      const r = clampRect(drag.startX, drag.startY, drag.endX, drag.endY);
      return {
        left: r.left * sx,
        top: r.top * sy,
        width: r.width * sx,
        height: r.height * sy,
      };
    }
    if (selection) {
      return {
        left: selection.left * sx,
        top: selection.top * sy,
        width: selection.width * sx,
        height: selection.height * sy,
      };
    }
    return null;
  };

  const saveCanvas = async (canvas) => {
    setSaving(true);
    setError('');
    try {
      const base = (file?.name || 'floor-plan').replace(/\.pdf$/i, '');
      const out = await canvasToPngFile(canvas, `${base}-page${pageNum}.png`);
      onConfirm(out);
    } catch (e) {
      console.error('[FloorPlanPdfCropModal] save failed', e);
      setError(e?.message || 'Failed to save image');
    } finally {
      setSaving(false);
    }
  };

  const useFullPage = () => {
    const canvas = sourceCanvasRef.current;
    if (!canvas) return;
    saveCanvas(canvas);
  };

  const useSelection = () => {
    const canvas = sourceCanvasRef.current;
    if (!canvas || !selection) return;
    const cropped = cropCanvas(canvas, selection.left, selection.top, selection.width, selection.height);
    saveCanvas(cropped);
  };

  const rect = overlayRect();

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-card rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 p-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-600 mt-1">
              Drag on the page to select an area, or use the full page. Saved as a compressed image for the floor plan.
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-auto flex-1">
          {numPages > 1 && (
            <label className="flex items-center gap-2 text-sm">
              <span className="text-gray-600">Page</span>
              <select
                value={pageNum}
                onChange={(e) => setPageNum(Number(e.target.value))}
                className="border rounded-lg px-2 py-1"
              >
                {Array.from({ length: numPages }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n} / {numPages}</option>
                ))}
              </select>
            </label>
          )}

          {loading && <p className="text-sm text-gray-500 py-8 text-center">Loading PDF…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          {!loading && !error && sourceCanvasRef.current && (
            <div
              ref={wrapRef}
              className="relative mx-auto border rounded-lg bg-slate-100 overflow-hidden select-none touch-none"
              style={{ width: displaySize.w, height: displaySize.h }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
            >
              <img
                src={sourceCanvasRef.current.toDataURL('image/png')}
                alt="PDF page"
                className="block w-full h-full"
                draggable={false}
              />
              {rect && (
                <div
                  className="absolute border-2 border-primary-500 bg-primary-500/20 pointer-events-none"
                  style={{
                    left: rect.left,
                    top: rect.top,
                    width: rect.width,
                    height: rect.height,
                  }}
                />
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border rounded-lg text-sm">
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || loading || !!error}
            onClick={useFullPage}
            className="inline-flex items-center gap-1.5 px-4 py-2 border rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <Maximize2 className="w-4 h-4" />
            Use full page
          </button>
          <button
            type="button"
            disabled={saving || !selection || loading}
            onClick={useSelection}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            <Crop className="w-4 h-4" />
            {saving ? 'Saving…' : 'Use selection'}
          </button>
        </div>
      </div>
    </div>
  );
}
